import type { AccountState } from '@/account/AccountService';
import type { LockCtaOptions } from '@/ui/lockCta';
import { BUY_GAME_LABEL } from '@/ui/lockCta';
import { nativeShims } from '@/native/shims';
import type { SocialEvent, SocialService } from '@/social/SocialService';
import {
  isCrewRaceFresh,
  joinCrewMessage,
  parseCrewRace,
  friendRequestMessage,
  type CrewRaceInvite,
  type SocialInbox,
} from '@/social/crewLogic';
import {
  captureSocialUrl,
  clearPendingCrewCode,
  clearPendingFriendCode,
  pendingCrewCode,
  pendingFriendCode,
} from '@/social/links';
import { CrewRaceToast } from '@/ui/social/CrewRaceToast';
import {
  openCrewLinkPrompt,
  openFriendLinkPrompt,
  openPushSoftAsk,
} from '@/ui/social/crewSheets';
import { sheetOpen } from '@/ui/social/sheet';
import { confirmOpen } from '@/ui/confirm';

/**
 * CrewController.ts — the RACE CREW glue that lives OUTSIDE the crew screen
 * (M140): it hears the live inbox and decides what reaches the player, when.
 *
 *  - CREW RACE INVITES: on the menus the pop-up drops in at once; in a race
 *    only the small side message shows (no buttons), and the pop-up waits
 *    for the race to end (a light poll while one is pending). An invite the
 *    player closed, joined, or that closed server-side is never shown again.
 *  - LINKS: a crew link or friend link that brought the player here is acted
 *    on once they are signed in, past onboarding, on a menu, and allowed to
 *    use crews (web: owners only — a non-owner's crew link sells the game
 *    and keeps the code until they own it).
 *  - iOS PUSH: the contextual soft-ask after joining / creating a crew, and a
 *    quiet token refresh on every sign-in once permission is granted. A
 *    tapped push opens the invite (the app was asleep, so realtime missed it).
 */

export type CrewMoment = 'menu' | 'racing' | 'blocked';

export interface CrewControllerBridge {
  social: SocialService;
  account(): AccountState;
  /** Where the player is: a menu (safe to pop), a race, or a flow that must
   *  not be interrupted (onboarding, the demo race, the purchase screen…). */
  moment(): CrewMoment;
  /** The room this player is currently in (a crew race already joined). */
  currentRoom(): string | null;
  /** May this player use crews right now? (web non-owner → the lock). */
  lock(): LockCtaOptions | undefined;
  /** Big Kid never sees any of this (no accounts, no online — M83). */
  kidsMode(): boolean;
  joinCrewRace(race: CrewRaceInvite): void;
  /** A crew link was accepted — open the crew screen and run the welcome
   *  (helmet pick) for the crew just joined. */
  showCrew(joinedCrew?: string): void;
  reducedMotion(): boolean;
  /** Entry-point badges (online hub card, dashboard button). */
  onBadge(count: number): void;
}

const PUSH_ASK_KEY = 'dribble-racer.pushask.v1';
const PUSH_TOKEN_KEY = 'dribble-racer.pushtoken.v1';

function store(key: string, v: string | null): void {
  try {
    if (v === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, v);
  } catch {
    /* storage blocked */
  }
}
function load(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export class CrewController {
  readonly toast: CrewRaceToast;
  private pending: CrewRaceInvite | null = null;
  private readonly seen = new Set<string>();
  /** How often each invite has popped (a race can interrupt it once). */
  private readonly popShows = new Map<string, number>();
  private sideShownFor: string | null = null;
  private poll: number | null = null;
  private inbox: SocialInbox | null = null;
  private linksBusy = false;
  private userId: string | null = null;
  /** A push tapped before the Racer ID resolved (a cold launch) — acted on
   *  as soon as the player is signed in. */
  private tappedRace: CrewRaceInvite | null = null;
  /** Crew links a web non-owner already dismissed this session (the BUY
   *  prompt): kept for after a purchase, never re-shown before one. */
  private readonly lockSnoozed = new Set<string>();
  /** Coalesces inbox refreshes triggered by live events (a burst of invites
   *  or requests becomes one round trip). */
  private inboxTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly b: CrewControllerBridge,
  ) {
    this.toast = new CrewRaceToast(parent);
    b.social.on((e) => this.onEvent(e));
    // iOS: pushes + universal links route here (set before native init can fire).
    nativeShims.onPushTap = (data) => this.onPush(data, true);
    nativeShims.onPushReceive = (data) => this.onPush(data, false);
    nativeShims.onLinkOpen = (url) => {
      if (captureSocialUrl(url)) this.maybeHandleLinks();
    };
  }

  // ---- account ---------------------------------------------------------------------

  onAccountState(st: AccountState): void {
    const uid = st.status === 'signedIn' ? st.userId : null;
    if (uid !== this.userId) {
      this.userId = uid;
      this.pending = null;
      this.seen.clear();
      this.popShows.clear();
      this.stopPoll();
      this.toast.hideAll();
      this.inbox = null;
      this.b.onBadge(0);
      if (uid) void this.refreshPushToken();
    }
    if (uid && this.tappedRace) {
      const race = this.tappedRace;
      this.tappedRace = null;
      this.openTapped(race);
    }
    this.maybeHandleLinks();
  }

  /** Sign-out tidy-up (AccountService.beforeSignOut): this device must stop
   *  receiving the old Racer ID's crew pings. */
  async beforeSignOut(): Promise<void> {
    this.toast.hideAll();
    this.pending = null;
    const tok = load(PUSH_TOKEN_KEY);
    if (tok) await this.b.social.unregisterPushToken(tok);
    await this.b.social.shutdown();
  }

  // ---- live events ----------------------------------------------------------------------

  private onEvent(e: SocialEvent): void {
    if (this.b.kidsMode()) return;
    switch (e.type) {
      case 'crew_race':
        this.offer(e.race);
        break;
      case 'crew_race_closed':
        if (this.pending?.id === e.id) this.pending = null;
        this.toast.hidePopup(e.id);
        this.seen.add(e.id);
        break;
      case 'inbox':
        this.inbox = e.inbox;
        this.b.onBadge(this.badgeCount());
        if (e.inbox.race) {
          this.offer(e.inbox.race);
        } else if (this.pending) {
          // The server says no race is open any more (closed / left the crew).
          this.toast.hidePopup(this.pending.id);
          this.pending = null;
        }
        break;
      case 'crew_invite':
      case 'friend_request':
        this.refreshInboxSoon();
        break;
      default:
        break;
    }
  }

  private refreshInboxSoon(): void {
    if (this.inboxTimer !== null) return;
    this.inboxTimer = window.setTimeout(() => {
      this.inboxTimer = null;
      void this.b.social.refreshInbox(true);
    }, 1500);
  }

  private badgeCount(): number {
    const i = this.inbox;
    if (!i) return 0;
    return i.crewInvites + i.friendRequests + (i.race && isCrewRaceFresh(i.race) ? 1 : 0);
  }

  /** A crew race invite arrived (live, from the inbox, or from a push). */
  private offer(race: CrewRaceInvite): void {
    if (!isCrewRaceFresh(race) || this.seen.has(race.id)) return;
    if (race.startedBy && race.startedBy === this.userId) return; // my own race
    if (this.b.currentRoom() === race.room) return; // already in it
    this.pending = race;
    this.tryShow();
    if (this.pending && this.poll === null) {
      // Waiting on a race to end / a flow to finish — check back lightly.
      this.poll = window.setInterval(() => this.tryShow(), 1500);
    }
  }

  /** Runs on every offer and every 1.5 s while an invite is pending, so the
   *  pop-up follows the player: it drops in on a menu, steps aside (and the
   *  side message shows instead) the moment a race starts under it, and comes
   *  back once when that race ends. */
  private tryShow(): void {
    const race = this.pending;
    if (!race || !isCrewRaceFresh(race) || this.seen.has(race.id)) {
      if (race) this.toast.hidePopup(race.id); // expired while on screen
      this.pending = null;
      this.stopPoll();
      return;
    }
    if (this.b.currentRoom() === race.room) {
      this.pending = null;
      this.stopPoll();
      this.toast.hidePopup(race.id);
      return;
    }
    const moment = this.b.moment();
    const showing = this.toast.popupRaceId === race.id;
    if (moment === 'racing') {
      // Never leave a JOIN button over a live race (owner rule).
      if (showing) this.toast.hidePopup(race.id);
      if (this.sideShownFor !== race.id) {
        this.sideShownFor = race.id;
        this.toast.showSide(race, this.b.reducedMotion());
      }
      return; // the pop-up waits for the race to end
    }
    if (moment === 'blocked') {
      if (showing) this.toast.hidePopup(race.id);
      return;
    }
    if (showing || sheetOpen() || confirmOpen()) return;
    const shows = (this.popShows.get(race.id) ?? 0) + 1;
    if (shows > 2) {
      // Shown twice already: it stays in RACE CREW (JOIN CREW RACE + the
      // LIVE badge) instead of chasing the player around the menus.
      this.seen.add(race.id);
      this.pending = null;
      this.stopPoll();
      return;
    }
    this.popShows.set(race.id, shows);
    const done = (): void => {
      this.seen.add(race.id);
      if (this.pending?.id === race.id) this.pending = null;
    };
    this.toast.showPopup(race, {
      reducedMotion: this.b.reducedMotion(),
      onJoin: () => {
        done();
        this.b.joinCrewRace(race);
      },
      onLater: done,
      onTimeout: done,
    });
  }

  private stopPoll(): void {
    if (this.poll !== null) {
      window.clearInterval(this.poll);
      this.poll = null;
    }
  }

  /** Flow: a crew race was joined/started some other way (the crew screen's
   *  JOIN, a push tap) — never pop it again. */
  markHandled(raceId: string): void {
    this.seen.add(raceId);
    if (this.pending?.id === raceId) this.pending = null;
    this.toast.hidePopup(raceId);
  }

  /** Flow: a menu just came up (title, results…) — a waiting invite can show. */
  nudge(): void {
    if (this.pending) this.tryShow();
    this.maybeHandleLinks();
  }

  // ---- links --------------------------------------------------------------------------------

  /** Act on a crew / friend link that brought the player here, once it can be. */
  maybeHandleLinks(): void {
    if (this.linksBusy || this.b.kidsMode()) return;
    const st = this.b.account();
    if (st.status !== 'signedIn' || st.onboarded !== true) return;
    // Never inside an online session (a lobby can auto-start under a modal);
    // the prompt waits for the title / a menu (nudge, account changes).
    if (this.b.moment() !== 'menu' || this.b.currentRoom() || sheetOpen() || confirmOpen()) return;
    const lock = this.b.lock();
    let crewCode = pendingCrewCode();
    if (crewCode && lock && this.lockSnoozed.has(crewCode)) crewCode = null;
    const friendCode = pendingFriendCode();
    if (!crewCode && !friendCode) return;
    if (crewCode) {
      this.linksBusy = true;
      void this.promptCrewLink(crewCode, lock);
      return;
    }
    if (friendCode && !lock) {
      this.linksBusy = true;
      void this.promptFriendLink(friendCode);
    }
  }

  private async promptCrewLink(code: string, lock: LockCtaOptions | undefined): Promise<void> {
    const p = await this.b.social.crewPreview(code);
    if (p.status !== 'ok') {
      // A dead/garbled link is dropped (an offline answer keeps it for later).
      if (p.status !== 'offline') clearPendingCrewCode();
      this.linksBusy = false;
      return;
    }
    if (p.myCrew && p.myCrew === p.crewId) {
      clearPendingCrewCode(); // already in it
      this.linksBusy = false;
      this.maybeHandleLinks();
      return;
    }
    // Joining means leaving the crew they're in — name it in the prompt.
    let leaving: string | undefined;
    if (p.myCrew) leaving = (await this.b.social.crewOverview())?.crew?.name ?? 'your club';
    openCrewLinkPrompt({
      crewName: p.name,
      crest: p.crest,
      members: p.members,
      captain: p.captain,
      leaving,
      reducedMotion: this.b.reducedMotion(),
      locked: lock
        ? {
            line: 'Racer Clubs are part of Dribble Racer. Get the game to join your friends.',
            buyLabel: `${BUY_GAME_LABEL} · ${lock.price}`,
            onBuy: () => {
              this.linksBusy = false;
              lock.onBuy();
            },
            onLogIn: lock.onLogIn
              ? () => {
                  this.linksBusy = false;
                  lock.onLogIn?.();
                }
              : undefined,
          }
        : undefined,
      onJoin: async () => {
        const r = await this.b.social.joinCrewByCode(code, !!p.myCrew);
        if (r.status !== 'ok') return joinCrewMessage(r.status);
        clearPendingCrewCode();
        this.linksBusy = false;
        this.b.showCrew(p.name);
        return null;
      },
      onDismiss: () => {
        // NOT NOW: forget the link (it's still in their messages), unless they
        // still have to buy the game — then it waits for after the purchase
        // (and stays quiet until then: no BUY nag on every menu).
        if (!lock) clearPendingCrewCode();
        else this.lockSnoozed.add(code);
        this.linksBusy = false;
      },
    });
  }

  private async promptFriendLink(code: string): Promise<void> {
    const p = await this.b.social.friendPreview(code);
    if (p.status !== 'ok' || p.isFriend) {
      if (p.status !== 'offline') clearPendingFriendCode();
      this.linksBusy = false;
      return;
    }
    openFriendLinkPrompt({
      name: p.name,
      helmet: p.helmet,
      xp: p.xp,
      reducedMotion: this.b.reducedMotion(),
      onAdd: async () => {
        const r = await this.b.social.addFriendByCode(code);
        if (r.status !== 'added' && r.status !== 'already') {
          return friendRequestMessage(r.status, p.name);
        }
        clearPendingFriendCode();
        this.linksBusy = false;
        return null;
      },
      onDismiss: () => {
        clearPendingFriendCode();
        this.linksBusy = false;
      },
    });
  }

  // ---- iOS push ---------------------------------------------------------------------------------

  /** After joining / creating a crew: the one-time contextual ask (iOS only). */
  maybeAskPush(crewName: string): void {
    const push = nativeShims.push;
    if (!push || load(PUSH_ASK_KEY)) return;
    void push.permission().then((perm) => {
      if (perm === 'granted') {
        void this.refreshPushToken();
        return;
      }
      if (perm === 'denied') return; // the system prompt was used; Settings owns it now
      openPushSoftAsk({
        crewName,
        reducedMotion: this.b.reducedMotion(),
        onYes: () => {
          store(PUSH_ASK_KEY, 'yes');
          void this.registerPush();
        },
        onNo: () => store(PUSH_ASK_KEY, 'no'),
      });
    });
  }

  /** Quiet: keep this device's token on the signed-in Racer ID (tokens can
   *  change, and a shared phone may have switched accounts). */
  private async refreshPushToken(): Promise<void> {
    const push = nativeShims.push;
    if (!push) return;
    try {
      if ((await push.permission()) !== 'granted') return;
      await this.registerPush();
    } catch {
      /* never fatal */
    }
  }

  private async registerPush(): Promise<void> {
    const push = nativeShims.push;
    if (!push) return;
    const token = await push.register();
    if (!token) return;
    const r = await this.b.social.registerPushToken(token, 'ios');
    if (r.status === 'ok') store(PUSH_TOKEN_KEY, token);
  }

  /** A push arrived (foreground) or was tapped (app opened from it). */
  private onPush(data: Record<string, unknown>, tapped: boolean): void {
    if (this.b.kidsMode()) return;
    const race = parseCrewRace(data.crewRace);
    if (!race) return;
    if (!tapped) {
      this.offer(race);
      return;
    }
    if (this.b.account().status !== 'signedIn') {
      this.tappedRace = race; // a cold launch: wait for the Racer ID
      return;
    }
    this.openTapped(race);
  }

  /** The player tapped the ping: go straight into the race if it is still
   *  open and nothing must not be interrupted; otherwise offer it. The server
   *  is asked first, so a race that already closed is never joined. */
  private openTapped(race: CrewRaceInvite): void {
    void this.b.social.refreshInbox(true).then((inbox) => {
      const live = inbox?.race && inbox.race.id === race.id ? inbox.race : null;
      if (!live || !isCrewRaceFresh(live)) return;
      if (this.b.moment() === 'menu' && this.b.currentRoom() !== live.room) {
        this.markHandled(live.id);
        this.b.joinCrewRace(live);
      } else {
        this.offer(live);
      }
    });
  }
}
